import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

// Inline SVG markers (sharp pin in Flame Orange + black user dot) keep us
// from depending on leaflet's broken default icon paths in CRA.
const studioIcon = L.divIcon({
    className: "",
    html: `<div style="
        width: 28px; height: 36px; transform: translate(-14px,-36px);
        display:flex; align-items:flex-start; justify-content:center;">
        <svg viewBox='0 0 24 32' width='28' height='36'
             xmlns='http://www.w3.org/2000/svg'>
            <path d='M12 0 C5 0 0 5 0 12 C0 22 12 32 12 32 C12 32 24 22 24 12 C24 5 19 0 12 0 Z'
                  fill='#FF4500' stroke='#1C1C1C' stroke-width='1.5'/>
            <circle cx='12' cy='12' r='4' fill='#fff'/>
        </svg>
    </div>`,
});
const userIcon = L.divIcon({
    className: "",
    html: `<div style="
        width: 24px; height: 24px; transform: translate(-12px,-12px);
        border-radius:9999px; background:#1C1C1C; border:3px solid #fff;
        box-shadow: 0 0 0 4px rgba(28,28,28,0.18);"></div>`,
});

// Black sharp pin for "live (unverified)" Google Places markers
const livePinIcon = L.divIcon({
    className: "",
    html: `<div style="
        width: 26px; height: 34px; transform: translate(-13px,-34px);
        display:flex; align-items:flex-start; justify-content:center;">
        <svg viewBox='0 0 24 32' width='26' height='34'
             xmlns='http://www.w3.org/2000/svg'>
            <path d='M12 0 C5 0 0 5 0 12 C0 22 12 32 12 32 C12 32 24 22 24 12 C24 5 19 0 12 0 Z'
                  fill='#1C1C1C' stroke='#FF4500' stroke-width='2'/>
            <circle cx='12' cy='12' r='3.5' fill='#FF4500'/>
        </svg>
    </div>`,
});

// "Starts soon" curated pin — orange with a pulsing flame badge to flag classes
// starting / opening within 60 min.
const startsSoonIcon = L.divIcon({
    className: "",
    html: `<div style="
        width: 34px; height: 42px; transform: translate(-17px,-42px);
        display:flex; align-items:flex-start; justify-content:center;
        position:relative;">
        <svg viewBox='0 0 24 32' width='30' height='38'
             xmlns='http://www.w3.org/2000/svg'>
            <path d='M12 0 C5 0 0 5 0 12 C0 22 12 32 12 32 C12 32 24 22 24 12 C24 5 19 0 12 0 Z'
                  fill='#FF4500' stroke='#1C1C1C' stroke-width='1.5'/>
            <circle cx='12' cy='12' r='4' fill='#fff'/>
        </svg>
        <span style="
            position:absolute; top:-4px; right:-2px;
            width:14px; height:14px; border-radius:9999px;
            background:#FFCC00; border:2px solid #1C1C1C;
            box-shadow: 0 0 0 0 rgba(255,204,0,0.7);
            animation: starts-soon-pulse 1.6s ease-out infinite;"></span>
    </div>`,
});

function FlyTo({ position, zoom = 14 }) {
    const map = useMap();
    useEffect(() => {
        if (position) map.flyTo(position, zoom, { duration: 0.8 });
    }, [position, zoom, map]);
    return null;
}

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (!points || points.length === 0) return;
        if (points.length === 1) {
            map.setView(points[0], 14, { animate: true });
            return;
        }
        const bounds = L.latLngBounds(points);
        // Pad so pins aren't flush against the edge.
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15, animate: true });
    }, [points, map]);
    return null;
}

function ResizeFix() {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 200);
        return () => clearTimeout(t);
    }, [map]);
    return null;
}

/**
 * MapView is now a thin presentation layer that renders the same `classes`
 * array the grid is showing — so any filter the user picks (category, search,
 * borough, audience, skill, radius, include-Google) flows through unchanged.
 *
 * Props:
 *   classes  : Array<row> — the same rows the grid is rendering
 *   userPos  : [lat,lng] | null — user's location (drives "you are here" pin)
 *   radius   : number | null — miles, used to draw the radius circle
 *   loading  : boolean — show "Loading…" overlay while parent is fetching
 *   onSelect, onClaim : callbacks for popup actions
 */
export function MapView({ classes = [], userPos, radius, loading = false, onSelect, onClaim }) {
    const [mapReady, setMapReady] = useState(false);

    // Defer MapContainer mount one tick to survive React StrictMode's
    // double-effect invocation that re-initializes the Leaflet container.
    useEffect(() => {
        const t = setTimeout(() => setMapReady(true), 0);
        return () => clearTimeout(t);
    }, []);

    // Split incoming rows into curated vs live overlays.
    const { curatedGrouped, liveGrouped, allPoints, startsSoonCount } = useMemo(() => {
        const curated = classes.filter((c) => !c.live_source);
        const live = classes.filter((c) => c.live_source === "google_places");
        const groupKey = (c) => `${c.latitude},${c.longitude}`;
        const groupByCoord = (arr) => {
            const map = new Map();
            for (const c of arr) {
                if (c.latitude == null || c.longitude == null) continue;
                const k = groupKey(c);
                if (!map.has(k)) map.set(k, []);
                map.get(k).push(c);
            }
            return Array.from(map.entries());
        };
        const cg = groupByCoord(curated);
        const lg = groupByCoord(live);
        const points = classes
            .filter((c) => c.latitude != null && c.longitude != null)
            .map((c) => [c.latitude, c.longitude]);
        const startsSoon = curated.filter((c) => c.class_status?.state === "starts_soon").length;
        return { curatedGrouped: cg, liveGrouped: lg, allPoints: points, startsSoonCount: startsSoon };
    }, [classes]);

    // Center: user position if known, else the centroid of the result set,
    // else fall back to Midtown.
    const initialCenter = userPos
        || (allPoints.length ? [allPoints[0][0], allPoints[0][1]] : [40.7549, -73.984]);

    return (
        <section className="px-6 md:px-12 py-10 border-t border-border" data-testid="map-section">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                <div>
                    <p className="font-mono-label text-xs text-muted-foreground mb-3">
                        Same filters as the grid · {classes.length} on map
                    </p>
                    <h2 className="font-display text-3xl sm:text-5xl">
                        Map <span className="font-editorial text-primary">view.</span>
                    </h2>
                </div>
                <p className="font-mono-label text-[10px] text-muted-foreground max-w-sm md:text-right">
                    Adjust filters, sort, or radius from the bar above. Pins here
                    auto-update to match.
                </p>
            </div>

            <div className="relative border border-foreground" style={{ height: "560px" }}>
                {mapReady && (
                <MapContainer
                    center={initialCenter}
                    zoom={13}
                    scrollWheelZoom
                    style={{ height: "100%", width: "100%", background: "#F0EFEB" }}
                    data-testid="leaflet-map"
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <ResizeFix />
                    {userPos ? (
                        <FlyTo position={userPos} zoom={14} />
                    ) : (
                        <FitBounds points={allPoints} />
                    )}
                    {userPos && (
                        <>
                            <Marker position={userPos} icon={userIcon}>
                                <Popup>
                                    <strong>You are here</strong>
                                </Popup>
                            </Marker>
                            {radius && (
                                <Circle
                                    center={userPos}
                                    radius={radius * 1609.34}
                                    pathOptions={{ color: "#FF4500", weight: 1.5, fillOpacity: 0.04 }}
                                />
                            )}
                        </>
                    )}
                    {curatedGrouped.map(([key, rows]) => {
                        const first = rows[0];
                        // If ANY class at this coordinate is "starts_soon", use
                        // the pulsing pin so users spot live activity instantly.
                        const hasStartsSoon = rows.some(
                            (r) => r.class_status?.state === "starts_soon"
                        );
                        const icon = hasStartsSoon ? startsSoonIcon : studioIcon;
                        return (
                            <Marker key={key} position={[first.latitude, first.longitude]} icon={icon}>
                                <Popup>
                                    <div className="font-body">
                                        <div className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                                            {first.category} · {first.neighborhood || first.borough}
                                        </div>
                                        <div className="font-display text-base font-bold leading-tight">
                                            {first.studio_name}
                                        </div>
                                        <div className="text-xs text-neutral-700 mt-1">
                                            {first.address}
                                        </div>
                                        {first.distance_miles != null && (
                                            <div className="text-xs text-neutral-500 mt-0.5">
                                                ≈ {first.distance_miles} mi away
                                            </div>
                                        )}
                                        {first.class_status && (
                                            <div className="mt-2">
                                                <StatusBadge status={first.class_status} size="compact" />
                                            </div>
                                        )}
                                        <div className="mt-2 space-y-1">
                                            {rows.slice(0, 4).map((r) => (
                                                <button
                                                    key={r.id}
                                                    className="block text-left text-xs underline decoration-[#FF4500] underline-offset-2 hover:text-[#FF4500]"
                                                    onClick={() => onSelect?.(r)}
                                                    data-testid={`popup-class-${r.id}`}
                                                >
                                                    {r.class_name}
                                                </button>
                                            ))}
                                            {rows.length > 4 && (
                                                <div className="text-[11px] text-neutral-500">
                                                    + {rows.length - 4} more
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                    {liveGrouped.map(([key, rows]) => {
                        const first = rows[0];
                        return (
                            <Marker key={`live-${key}`} position={[first.latitude, first.longitude]} icon={livePinIcon}>
                                <Popup>
                                    <div className="font-body">
                                        <div className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                                            Live · Google Places · {first.category}
                                        </div>
                                        <div className="font-display text-base font-bold leading-tight">
                                            {first.studio_name}
                                        </div>
                                        <div className="text-xs text-neutral-700 mt-1">
                                            {first.address}
                                        </div>
                                        {first.rating != null && (
                                            <div className="text-xs text-neutral-700 mt-0.5">
                                                ★ {first.rating}
                                                {first.rating_count ? ` (${first.rating_count})` : ""}
                                            </div>
                                        )}
                                        {first.distance_miles != null && (
                                            <div className="text-xs text-neutral-500 mt-0.5">
                                                ≈ {first.distance_miles} mi away
                                            </div>
                                        )}
                                        <div className="mt-2 text-[10px] uppercase tracking-widest text-orange-600 font-bold">
                                            UNVERIFIED — confirm women-focus / classes
                                        </div>
                                        <button
                                            className="mt-2 block text-left text-xs underline decoration-[#FF4500] underline-offset-2 hover:text-[#FF4500]"
                                            onClick={() => onSelect?.(first)}
                                            data-testid={`live-popup-${first.id}`}
                                        >
                                            View details
                                        </button>
                                        <button
                                            className="mt-1 block text-left text-xs font-bold uppercase tracking-widest text-[#1C1C1C] hover:text-[#FF4500]"
                                            onClick={() => onClaim?.(first)}
                                            data-testid={`claim-live-${first.id}`}
                                        >
                                            Are you the owner? Claim →
                                        </button>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
                )}
                {!mapReady && (
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading map…
                    </div>
                )}
                {loading && mapReady && (
                    <div className="absolute top-4 right-4 bg-background border border-foreground px-3 py-2 flex items-center gap-2 text-xs font-mono-label">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Updating pins…
                    </div>
                )}
                {classes.length === 0 && mapReady && !loading && (
                    <div className="absolute inset-x-0 bottom-16 mx-auto w-fit bg-background border border-foreground px-4 py-3 text-xs font-mono-label uppercase tracking-widest">
                        No matches with current filters
                    </div>
                )}
                <div className="absolute bottom-4 left-4 bg-background border border-foreground px-3 py-2 flex items-center gap-3 text-xs font-mono-label" data-testid="map-legend">
                    <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-primary" /> Curated ({curatedGrouped.length})
                    </span>
                    {startsSoonCount > 0 && (
                        <span className="flex items-center gap-1" data-testid="legend-starts-soon">
                            <span className="relative inline-flex">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#FFCC00] border border-foreground" />
                            </span>
                            Starts soon ({startsSoonCount})
                        </span>
                    )}
                    {liveGrouped.length > 0 && (
                        <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-foreground" /> Live ({liveGrouped.length})
                        </span>
                    )}
                    {userPos && (
                        <span className="flex items-center gap-1">
                            <span className="inline-block h-2.5 w-2.5 rounded-full bg-foreground" /> You
                        </span>
                    )}
                </div>
            </div>
        </section>
    );
}
